import { useColorMode } from "@chakra-ui/react";
import { getGridClassName, IdContainer, TcmEncounter } from "@metriport/shared-internal";
import {
  ClientSideRowModelModule,
  ColDef,
  ColumnState,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  RowClickedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css"; // React Grid Logic
import "ag-grid-community/styles/ag-theme-quartz.css"; // Core CSS
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef } from "react";
import { formatDateTimeInLocal } from "../../../shared/date";
import "../grid.css";
import { TextCellEditor } from "./text-cell-editor";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

export default function TransitionsOfCareGrid({
  rows,
  search,
  isLoading,
  onRowClick,
  onNoteUpdate,
}: {
  rows: TcmEncounter[] | null;
  search: string;
  isLoading: boolean;
  onRowClick?: (row: TcmEncounter) => void;
  onNoteUpdate?: (encounterId: string, note: string) => void;
}) {
  const { colorMode } = useColorMode();
  const gridRef = useRef<AgGridReact>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const columnStateRef = useRef<ColumnState[] | null>(null);
  const isRestoringStateRef = useRef(false);
  const previousRowsRef = useRef<TcmEncounter[] | null>(null);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
  }, []);

  const onColumnStateChanged = useCallback(() => {
    if (gridApiRef.current && !isRestoringStateRef.current) {
      const state = gridApiRef.current.getColumnState();
      columnStateRef.current = state;
    }
  }, []);

  useEffect(() => {
    const currentRows = rows;
    const previousRows = previousRowsRef.current;

    if (
      gridApiRef.current &&
      columnStateRef.current &&
      currentRows !== previousRows &&
      currentRows
    ) {
      isRestoringStateRef.current = true;

      setTimeout(() => {
        if (gridApiRef.current && columnStateRef.current) {
          gridApiRef.current.applyColumnState({
            state: columnStateRef.current,
            applyOrder: true,
          });
        }
        isRestoringStateRef.current = false;
      }, 10);
    }

    previousRowsRef.current = currentRows;
  }, [rows]);

  const columnDefs: ColDef[] = [
    {
      field: "patientId",
      headerName: "Patient ID",
      cellClass: "clickable-cell",
      cellRenderer: (params: { value: string }) => (
        <IdContainer value={params.value} entity="Patient" full={true} />
      ),
      maxWidth: 400,
    },
    { field: "patientName", headerName: "Patient Name", maxWidth: 350 },
    { field: "patientDateOfBirth", headerName: "DOB", maxWidth: 150 },
    { field: "facilityName", headerName: "Facility Name", maxWidth: 250 },
    { field: "latestEvent", headerName: "Latest Event", maxWidth: 150 },
    { field: "class", headerName: "Class", maxWidth: 150 },
    {
      field: "admitTime",
      headerName: "Admit Time",
      maxWidth: 250,
      cellRenderer: (params: { value: Date | null | undefined }) =>
        formatDateTimeInLocal(params.value),
    },
    {
      field: "dischargeTime",
      headerName: "Discharge Time",
      maxWidth: 250,
      sort: "desc",
      cellRenderer: (params: { value: Date | null | undefined }) =>
        formatDateTimeInLocal(params.value),
    },
    {
      field: "freetextNote",
      headerName: "Notes",
      maxWidth: 250,
      editable: true,
      cellRenderer: (params: { value: string | null | undefined }) => params.value ?? "",
      cellClass: "editable-cell",
      cellEditor: TextCellEditor,
      cellEditorParams: {
        onNoteUpdate,
      },
    },
  ];

  const defaultColDef: ColDef = {
    flex: 1,
  };

  const handleRowClick = useCallback(
    (event: RowClickedEvent) => {
      const clickedElement = event.event?.target as HTMLElement;
      const hasNoRowClick = clickedElement.closest(".editable-cell");

      if (hasNoRowClick) {
        return;
      }

      onRowClick?.(event.data);
    },
    [onRowClick]
  );

  return (
    <>
      <AgGridReact
        ref={gridRef}
        className={getGridClassName(colorMode)}
        loading={isLoading}
        headerHeight={60}
        groupDefaultExpanded={-1}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={rows}
        quickFilterText={search}
        pagination={true}
        paginationPageSize={20}
        suppressCellFocus={true}
        onGridReady={onGridReady}
        onColumnMoved={onColumnStateChanged}
        onSortChanged={onColumnStateChanged}
        onRowClicked={handleRowClick}
        animateRows={false}
        suppressRowTransform={true}
        rowClass="cursor-pointer"
        singleClickEdit={true}
      />
    </>
  );
}
